import { ColumnWidth, TableColumn } from './types';

/**
 * Convert a column width specification to a CSS value
 */
export function getColumnWidth(width: ColumnWidth | undefined): string {
  if (width === undefined || width === 'flexible') {
    return 'auto';
  }
  
  if (typeof width === 'number') {
    return `${width}px`;
  }
  
  // Predefined width categories
  switch (width) {
    case 'extra-narrow':
      return '60px';
    case 'narrow':
      return '100px';
    case 'medium':
      return '150px';
    case 'wide':
      return '250px';
    default:
      return 'auto';
  }
}

/**
 * Get the appropriate text alignment for a column
 */
export function getColumnAlignment(column: TableColumn): string {
  if (column.align) {
    return `text-${column.align}`;
  }
  
  // Default alignments based on column type
  if (column.id.includes('amount') || column.id.includes('price') || column.id.includes('total')) {
    return 'text-right';
  }
  
  if (column.id.includes('date') || column.id.includes('status') || column.id.includes('actions') || column.id.includes('edit')) {
    return 'text-center';
  }
  
  return 'text-left';
}

/**
 * Get cell content from a row using the column accessor
 */
export function getCellContent<T>(row: T, column: TableColumn<T>): React.ReactNode {
  if (column.accessor) {
    return column.accessor(row);
  }
  
  // Fallback to using the column id as a key in the row object
  return (row as any)[column.id] ?? '-';
}

/**
 * Get the CSS class for a table cell
 */
export function getCellClassName(column: TableColumn, isHeader: boolean = false): string {
  // Use less horizontal padding for extra-narrow columns
  const baseClass = column.width === 'extra-narrow' ? 'px-2 py-2 ' : 'px-4 py-2 ';
  const alignmentClass = getColumnAlignment(column);
  
  if (isHeader) {
    return `${baseClass} ${alignmentClass} font-medium text-xs uppercase tracking-wider text-gray-500 ${column.headerClassName || ''}`;
  }
  
  return `${baseClass} ${alignmentClass} ${column.cellClassName || ''}`;
}

/**
 * Get the sort indicator for a column
 * Returns an empty string as the actual indicators are now handled via CSS
 */
export function getSortIndicator(columnId: string, sortField: string | null, sortDirection: 'asc' | 'desc'): string {
  // Return empty string as we're using CSS for the indicators now
  return '';
}

/**
 * Get the data-sort attribute value for a sortable column
 */
export function getSortAttribute(columnId: string, sortField: string | null, sortDirection: 'asc' | 'desc'): string | undefined {
  if (columnId !== sortField) {
    return undefined;
  }
  
  return sortDirection;
}
